import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheck } from 'react-icons/fa';

const ContactForm = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      setFormError(true);
      setErrorMessage('Please fill in all required fields marked with *');
      return;
    }
    
    setIsSubmitting(true);
    setFormError(false);
    
    // Prepare data for Web3Forms using the user's access key
    const formDataToSend = {
      access_key: '43b65445-0fe5-4c0d-9fae-b0e9df46a2a9',
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service: formData.service,
      message: formData.message,
      subject: `Quote Request: ${formData.service}`,
      from_url: window.location.origin,
      botcheck: '' // This will be empty for real users
    };
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formDataToSend)
      });
      
      const data = await response.json();
      
      if (response.status === 200) {
        // Success
        setFormSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        
        // Google Ads conversion tracking
        if (typeof gtag !== 'undefined') {
          // eslint-disable-next-line no-undef
          gtag('event', 'conversion', {
            'send_to': 'AW-16997036761/zoYoCJ_d8bYaENnl6Kg_',
            'value': 1.0,
            'currency': 'USD'
          });
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setFormSubmitted(false);
        }, 5000);
      } else {
        // Error
        console.log(response);
        setFormError(true);
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError(true);
      setErrorMessage('Network error. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <ContactSection className="section" id="contact-section">
      <div className="container">
        <ContactWrapper>
          <ContactInfo>
            <div className="section-title">
              <h2>Contact Us</h2>
              <p>Get in touch for a free quote or to discuss your project</p>
            </div>
            
            <InfoText>
              Our team of demolition and site clearing experts is ready to help with your next project. Contact us today for a free consultation and estimate.
            </InfoText>
            
            <ContactDetails>
              <ContactItem>
                <IconWrapper>
                  <FaPhone />
                </IconWrapper>
                <div>
                  <ContactLabel>Phone</ContactLabel>
                  <ContactValue>
                    <a href="tel:+13868722144">(386) 872-2144</a><br/>
                    <a href="tel:+15127674123">(512) 767-4123</a>
                  </ContactValue>
                </div>
              </ContactItem>
              
              <ContactItem>
                <IconWrapper>
                  <FaEnvelope />
                </IconWrapper>
                <div>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue><a href="mailto:FLSITESERVICE@GMAIL.COM">FLSITESERVICE@GMAIL.COM</a></ContactValue>
                </div>
              </ContactItem>
              
              <ContactItem>
                <IconWrapper>
                  <FaMapMarkerAlt />
                </IconWrapper>
                <div>
                  <ContactLabel>Location</ContactLabel>
                  <ContactValue>Crescent City, FL 32112<br/>Service-based business – we come to you!</ContactValue>
                </div>
              </ContactItem>
            </ContactDetails>
            
            <WhyChooseUs>
              <h3>Why Choose Us</h3>
              <ChooseUsList>
                <ChooseUsItem>
                  <FaCheck />
                  <span>15+ years of experience</span>
                </ChooseUsItem>
                <ChooseUsItem>
                  <FaCheck />
                  <span>Fully licensed and insured</span>
                </ChooseUsItem>
                <ChooseUsItem>
                  <FaCheck />
                  <span>Competitive pricing</span>
                </ChooseUsItem>
                <ChooseUsItem>
                  <FaCheck />
                  <span>Professional and reliable service</span>
                </ChooseUsItem>
                <ChooseUsItem>
                  <FaCheck />
                  <span>Free estimates</span>
                </ChooseUsItem>
              </ChooseUsList>
            </WhyChooseUs>
          </ContactInfo>
          
          <FormContainer id="quote-form">
            <h3>Get a Free Quote</h3>
            
            {formSubmitted ? (
              <SuccessMessage>
                <FaCheck />
                <div>
                  <p>Thank you for contacting us!</p>
                  <p>We've received your information and will get back to you shortly.</p>
                  <p className="note">For immediate assistance, please call us at <a href="tel:+13868722144">(386) 872-2144</a> or email us directly at <a href="mailto:FLSITESERVICE@GMAIL.COM">FLSITESERVICE@GMAIL.COM</a>.</p>
                </div>
              </SuccessMessage>
            ) : (
              <Form ref={formRef} onSubmit={handleSubmit}>
                {formError && (
                  <ErrorMessage>
                    <p>{errorMessage}</p>
                  </ErrorMessage>
                )}
                
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                
                <FormGroup>
                  <FormLabel htmlFor="name">Your Name*</FormLabel>
                  <FormInput
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="email">Email Address*</FormLabel>
                    <FormInput
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="phone">Phone Number*</FormLabel>
                    <FormInput
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <FormLabel htmlFor="service">Service Needed*</FormLabel>
                  <FormSelect
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="Demolition">Demolition</option>
                    <option value="Site Clearing">Site Clearing</option>
                    <option value="Concrete Removal">Concrete Removal</option>
                    <option value="Tree Removal">Tree & Stump Removal</option>
                    <option value="Other">Other</option>
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="message">Project Details</FormLabel>
                  <FormTextarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Tell us about your project..."
                  />
                </FormGroup>
                
                <FormButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </FormButton>
                
                <FormDisclaimer>
                  By submitting this form, you agree to our privacy policy and terms of service.
                </FormDisclaimer>
              </Form>
            )}
          </FormContainer>
        </ContactWrapper>
      </div>
    </ContactSection>
  );
};

const ContactSection = styled.section`
  background-color: var(--background-color);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95)), url('/images/services/A_realistic_construction_site_in_the_early_stages.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    opacity: 0.2;
    z-index: 0;
  }
  
  & > .container {
    position: relative;
    z-index: 1;
  }
`;

const ContactWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
`;

const ContactInfo = styled.div`
  .section-title {
    text-align: left;
    
    h2 {
      color: var(--primary-color);
    }
    
    h2::after {
      left: 0;
      transform: none;
      background: var(--primary-color);
    }
    
    p {
      margin: 0;
      color: var(--text-color);
    }
  }
`;

const InfoText = styled.p`
  font-size: 1.6rem;
  color: var(--text-color);
  margin: 2.5rem 0;
`;

const ContactDetails = styled.div`
  margin: 3rem 0;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const IconWrapper = styled.div`
  width: 5rem;
  height: 5rem;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2rem;
  
  svg {
    color: var(--background-color);
    font-size: 2rem;
  }
`;

const ContactLabel = styled.p`
  font-size: 1.4rem;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
  opacity: 0.7;
`;

const ContactValue = styled.p`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-color);
  
  a {
    color: var(--primary-color);
    
    &:hover {
      color: var(--secondary-color);
    }
  }
`;

const WhyChooseUs = styled.div`
  margin-top: 4rem;
  padding: 3rem;
  border-radius: 10px;
  background: linear-gradient(145deg, #1a1a1a, #2a2a2a);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 140, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(255, 140, 0, 0.3);
    border-color: rgba(255, 140, 0, 0.3);
  }
  
  h3 {
    font-size: 2.4rem;
    margin-bottom: 2rem;
    color: var(--primary-color);
  }
`;

const ChooseUsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ChooseUsItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  color: var(--text-color);
  
  svg {
    color: var(--primary-color);
    margin-right: 1.5rem;
    font-size: 1.6rem;
  }
`;

const FormContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #2a2a2a);
  padding: 4rem;
  border-radius: 10px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 140, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(255, 140, 0, 0.3);
    border-color: rgba(255, 140, 0, 0.3);
  }
  
  h3 {
    font-size: 2.8rem;
    margin-bottom: 3rem;
    text-align: center;
    color: var(--primary-color);
  }
  
  @media (max-width: 576px) {
    padding: 3rem 2rem;
  }
`;

const Form = styled.form`
  
`;

const FormGroup = styled.div`
  margin-bottom: 2.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: var(--text-color);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: 1px solid rgba(255, 140, 0, 0.3);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--text-color);
  border-radius: 5px;
  font-size: 1.6rem;
  font-family: var(--font-main);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.2);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: 1px solid rgba(255, 140, 0, 0.3);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--text-color);
  border-radius: 5px;
  font-size: 1.6rem;
  font-family: var(--font-main);
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23FF8C00' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.5rem center;
  background-size: 1.6rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.2);
  }
  
  option {
    background-color: var(--background-color);
    color: white;
    font-size: 1.6rem;
    padding: 1rem;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: 1px solid rgba(255, 140, 0, 0.3);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--text-color);
  border-radius: 5px;
  font-size: 1.6rem;
  font-family: var(--font-main);
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.2);
  }
`;

const FormButton = styled.button`
  width: 100%;
  padding: 1.5rem;
  background: linear-gradient(to right, var(--primary-color), #FF5500);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 140, 0, 0.3);
  
  &:hover {
    background: linear-gradient(to right, #FF5500, var(--primary-color));
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(255, 140, 0, 0.4);
  }
`;

const FormDisclaimer = styled.p`
  font-size: 1.2rem;
  text-align: center;
  color: var(--text-color);
  opacity: 0.7;
  margin-top: 2rem;
`;

const SuccessMessage = styled.div`
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  padding: 2.5rem 2rem;
  border-radius: 5px;
  display: flex;
  align-items: flex-start;
  
  svg {
    color: #28a745;
    font-size: 2.4rem;
    margin-right: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.3rem;
  }
  
  p {
    font-size: 1.6rem;
    color: var(--text-color);
    margin: 0 0 1rem 0;
    
    &:first-of-type {
      color: #28a745;
      font-weight: 600;
      font-size: 1.8rem;
    }
    
    &.note {
      font-size: 1.4rem;
      margin-top: 1.5rem;
      font-style: italic;
      opacity: 0.8;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  padding: 2rem;
  border-radius: 5px;
  margin-bottom: 2.5rem;
  
  p {
    font-size: 1.6rem;
    color: #dc3545;
    margin: 0;
  }
`;

export default ContactForm;